import React from "react";

const LoadingSpinner = ({
  size = "medium",
  text,
  tone = "primary",
  inline = false,
  className = "",
}) => {
  // Ultra-precise pixel dimensions matching native mobile standards
  const dimensions = {
    xs: { ring: "w-4 h-4", stroke: 2, glow: "blur-xs" },
    small: { ring: "w-5 h-5", stroke: 2.5, glow: "blur-sm" },
    medium: { ring: "w-8 h-8", stroke: 3, glow: "blur-md" },
    large: { ring: "w-11 h-11", stroke: 3.5, glow: "blur-lg" },
  };

  const current = dimensions[size] || dimensions.medium;

  // Tone color configurations matching brand palette
  const toneMap = {
    primary: {
      track: "text-emerald-500/15 dark:text-emerald-500/20",
      head: "text-emerald-600 dark:text-emerald-500",
      glow: "bg-emerald-500/20",
    },
    accent: {
      track: "text-amber-500/15 dark:text-amber-500/20",
      head: "text-amber-600 dark:text-amber-500",
      glow: "bg-amber-500/20",
    },
    muted: {
      track: "text-slate-400/15 dark:text-slate-600/20",
      head: "text-slate-500 dark:text-slate-400",
      glow: "bg-slate-400/10",
    },
    "on-dark": {
      track: "text-white/15",
      head: "text-white",
      glow: "bg-white/20",
    },
  };

  const colors = toneMap[tone] || toneMap.primary;

  const NativeRing = () => (
    <div className={`relative flex items-center justify-center ${current.ring} ${className}`}>
      {/* Background Track */}
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 32 32">
        <circle
          cx="16"
          cy="16"
          r="13"
          fill="none"
          strokeWidth={current.stroke}
          stroke="currentColor"
          className={colors.track}
        />
        {/* Animated Head Arc */}
        <circle
          cx="16"
          cy="16"
          r="13"
          fill="none"
          strokeWidth={current.stroke}
          strokeDasharray="81.68"
          strokeDashoffset="58"
          strokeLinecap="round"
          stroke="currentColor"
          className={`${colors.head} animate-spin origin-center duration-700`}
        />
      </svg>
      {/* Ambient Native Glow */}
      <div className={`absolute inset-0 rounded-full ${colors.glow} ${current.glow} pointer-events-none opacity-60`} />
    </div>
  );

  if (inline) {
    return (
      <div className="inline-flex items-center gap-2.5 select-none" aria-busy="true" role="status">
        <NativeRing />
        {text && (
          <span className="text-xs font-medium text-slate-600 dark:text-slate-300 tracking-wide">
            {text}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center py-6 text-center select-none min-h-[140px] animate-fade-in"
      aria-busy="true"
      role="status"
    >
      <NativeRing />
      {text && (
        <p className="mt-3.5 text-xs font-medium tracking-widest uppercase text-slate-500 dark:text-slate-400/90">
          {text}
        </p>
      )}
    </div>
  );
};

export default LoadingSpinner;